import type { IAuthSDKService } from '../model/IAuthSDKService';
import { ELocalStorageKeys } from '~/features/store/ELocalStorageKeys';
import { useLocalStorage } from '@vueuse/core';
import dayjs from 'dayjs';
import type { Ref } from 'vue';
import { ErrorBus } from '~/features/error-bus';

export class TokenManager {
  private readonly api: IAuthSDKService;

  #accessToken: Ref<string>;
  #refreshToken: Ref<string>;
  #accessTokenExpiredAt: Ref<string>;

  #refreshPromise: Promise<void> | null;

  constructor(api: IAuthSDKService) {
    this.api = api;

    this.#accessToken = useLocalStorage(ELocalStorageKeys.MM__AUTH_SDK__ACCESS_TOKEN, '', {
      listenToStorageChanges: false,
      writeDefaults: false,
    });

    this.#refreshToken = useLocalStorage(ELocalStorageKeys.MM__AUTH_SDK__REFRESH_TOKEN, '', {
      listenToStorageChanges: false,
      writeDefaults: false,
    });

    this.#accessTokenExpiredAt = useLocalStorage(ELocalStorageKeys.MM__AUTH_SDK__TOKEN_EXPIRE, '', {
      listenToStorageChanges: false,
      writeDefaults: false,
    });

    this.#refreshPromise = null;
  }

  public setTokens(accessToken: string, refreshToken: string) {
    this.#accessToken.value = accessToken;
    this.#refreshToken.value = refreshToken;

    const exp = this.parseJWT(accessToken)?.exp;

    if (exp) {
      // Вычесть из exp 1 минуту
      const expiredAt = dayjs(exp * 1000 - 60000).valueOf().toString();
      this.#accessTokenExpiredAt.value = expiredAt;
    }
  }

  public clearTokens() {
    this.#accessToken.value = '';
    this.#refreshToken.value = '';
    this.#accessTokenExpiredAt.value = '';
  }

  public isValid() {
    if (!this.#accessToken.value) return false;

    const now = dayjs();
    const expiredAt = dayjs(+this.#accessTokenExpiredAt.value);

    return expiredAt > now;
  }

  public isAuthorized() {
    return !!this.#accessToken.value;
  }

  public refresh() {
    if (this.#refreshPromise) return this.#refreshPromise;

    this.#refreshPromise = this.api
      .refreshToken({ refreshToken: this.#refreshToken.value })
      .then((response) => this.setTokens(response.accessToken, response.refreshToken))
      .catch((error) => {
        ErrorBus.emitError({
          e: error as Error,
          emitter: 'AuthSDK.TokenManager.refresh',
        });
      })
      .finally(() => (this.#refreshPromise = null));

    return this.#refreshPromise;
  }

  public async getToken() {
    if (!this.isAuthorized()) return null;

    if (this.isValid()) return this.#accessToken.value;

    await this.refresh();
    return this.#accessToken.value;
  }

  private parseJWT(token: string): { exp: number; iat: number; sub: string; phone: string } | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      ErrorBus.emitError({
        e: error as Error,
        emitter: 'AuthSDK.TokenManager.parseJWT',
      });

      throw error;
    }
  }

  public DEBUG_SET_EXPIRED_AT(exp: string) {
    this.#accessTokenExpiredAt.value = exp;
  }
}
